import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { fetchProgress, fetchWorksheetSummaries } from "../lib/workbookApi";
import { api, formatError } from "../lib/api";
import { MODULE_CHECKLISTS } from "../data/moduleChecklists";
import { useMenuVisibility } from "../context/MenuVisibilityContext";
import {
  Search, Filter, ChevronRight, Sparkles, ListChecks, CheckCircle2, CircleDot, Circle,
  ClipboardList, ArrowRight, Lock, Zap,
} from "lucide-react";

const DIFFICULTIES = ["Beginner", "Intermediate", "Advanced"];

/**
 * Build the "Open" target for a workbook — always inside the parent
 * Module page's Worksheets tab, so founders stay in the module's
 * context (video, lessons, decisions, checklist) instead of being
 * yanked to a standalone surface.
 */
function openWorkbookHref(mod) {
  return `/modules/${encodeURIComponent(mod.lms_slug)}?tab=worksheets`;
}

/**
 * Resolve the per-user action state for a workbook card. Mirrors the
 * exact gating logic that `/modules` + `Course.jsx` use so changes to
 * plan tiers or Menu-Directory settings propagate here automatically.
 *
 *   • `open`    — module unlocked for this user; CTA opens the module
 *                 page scrolled to its Worksheets tab.
 *   • `unlock`  — plan-included but progress-locked (sequential_core /
 *                 bonus_gated). CTA is a safe link to the gating module
 *                 so founders know exactly what to finish next.
 *   • `upgrade` — plan-locked (plan_gated or free-tier module shortage);
 *                 CTA routes to /account/subscription.
 *
 * Falls through to `open` when the module registry hasn't loaded yet so
 * network blips don't blank out the card action.
 */
function resolveCardAction(mod, canonical) {
  if (!canonical) {
    return { kind: "open", to: openWorkbookHref(mod) };
  }
  if (!canonical.locked) {
    return { kind: "open", to: openWorkbookHref(mod) };
  }
  const reason = canonical.locked_reason || {};
  if (reason.kind === "sequential_core") {
    return {
      kind: "unlock",
      label: `Unlock · Complete Module ${reason.unlock_after_number}`,
      to: reason.unlock_after_slug ? `/modules/${reason.unlock_after_slug}` : "/course",
    };
  }
  if (reason.kind === "bonus_gated") {
    return {
      kind: "unlock",
      label: "Unlock · Complete all Core Modules",
      to: "/course",
    };
  }
  // plan_gated OR any other lock (free tier short of min_tier) → upgrade path
  return { kind: "upgrade", to: "/account/subscription" };
}

function CompletionBar({ pct }) {
  return (
    <div className="w-full h-1.5 rounded-full bg-white/10 overflow-hidden">
      <div
        className="h-full rounded-full bg-[color:var(--brand-accent,#E91E63)] transition-[width] duration-500"
        style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

function StatusDot({ status }) {
  if (status === "submitted")
    return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />;
  if (status === "in_progress")
    return <CircleDot className="w-3.5 h-3.5 text-pink-400 shrink-0" />;
  return <Circle className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />;
}

function ModuleCard({ mod, checklist, action }) {
  const pct = Math.round(mod.completion_pct || 0);
  const checklistTotal = checklist?.total || 0;
  const checklistDone = checklist?.done || 0;
  // Only the "open" state lets the whole card navigate — "unlock" and
  // "upgrade" require the explicit CTA so the plan/progress intent is
  // unambiguous (clicking the card body would feel like a trap). On
  // `unlock`/`upgrade` the card renders as a static panel with the
  // CTA button handling navigation.
  const CardTag = action.kind === "open" ? Link : "div";
  const cardProps = action.kind === "open"
    ? { to: action.to, className: "block rounded-xl border border-border bg-white hover:bg-muted/60 p-5 transition group" }
    : { className: "rounded-xl border border-border bg-white p-5 relative" };
  return (
    <CardTag {...cardProps} data-testid={`workbook-module-${mod.module_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85">
            {mod.is_bonus ? "Bonus" : `Module ${mod.module_number}`}
          </div>
          <h3 className={[
            "mt-1 font-display text-lg leading-snug transition",
            action.kind === "open" ? "group-hover:text-[color:var(--brand-accent,#E91E63)]" : "",
          ].join(" ")}>
            {mod.title}
          </h3>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold">{pct}%</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85">
            {mod.worksheets_submitted}/{mod.worksheets_total} done
          </div>
        </div>
      </div>
      <div className="mt-3">
        <CompletionBar pct={pct} />
      </div>
      {checklistTotal > 0 && (
        <div
          className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground"
          data-testid={`workbook-checklist-counter-${mod.module_id}`}
        >
          <ClipboardList className="w-3 h-3 shrink-0 text-primary/80" />
          <span className="font-medium tabular-nums">
            {checklistDone}/{checklistTotal}
          </span>
          <span>action items checked</span>
        </div>
      )}
      <ul className="mt-4 space-y-1.5">
        {(mod.worksheets || []).map((w) => (
          <li key={w.worksheet_id} className="flex items-center gap-2">
            <StatusDot status={w.status} />
            <span className="text-xs text-foreground/70 truncate">{w.title}</span>
          </li>
        ))}
      </ul>
      <CardAction action={action} moduleId={mod.module_id} />
    </CardTag>
  );
}

function CardAction({ action, moduleId }) {
  // Hoisted into its own component so each action variant can own its
  // icon + copy without bloating ModuleCard. Button spans full width
  // on mobile and sits inline on sm+.
  if (action.kind === "open") {
    return (
      <div
        className="mt-5 flex items-center justify-end text-sm font-semibold text-[color:var(--brand-accent,#E91E63)] gap-1 group-hover:gap-2 transition-all"
        data-testid={`workbook-module-${moduleId}-open`}
      >
        Open <ArrowRight className="w-4 h-4" />
      </div>
    );
  }
  if (action.kind === "unlock") {
    return (
      <Link
        to={action.to}
        className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-muted/70 px-3 py-2 text-xs sm:text-sm font-semibold text-muted-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        data-testid={`workbook-module-${moduleId}-unlock`}
      >
        <Lock className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{action.label}</span>
      </Link>
    );
  }
  // upgrade
  return (
    <Link
      to={action.to}
      className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[color:var(--brand-accent,#E91E63)] px-3 py-2 text-xs sm:text-sm font-semibold text-white hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      data-testid={`workbook-module-${moduleId}-upgrade`}
    >
      <Zap className="w-3.5 h-3.5" /> Upgrade
    </Link>
  );
}

function WorksheetRow({ w, statusByWs, action }) {
  const status = (statusByWs && statusByWs[w.worksheet_id]) || "not_started";
  const kind = action?.kind || "open";
  const openHref = action?.to || `/workbook/${w.worksheet_id}`;

  // When the parent module is locked (progress or plan), the whole row
  // becomes a non-link surface with an explicit CTA so founders can't
  // accidentally land on a worksheet they can't open.
  const RowTag = kind === "open" ? Link : "div";
  const rowProps = kind === "open"
    ? { to: openHref, className: "flex items-center gap-4 p-4 rounded-lg border border-border bg-white hover:bg-muted/50 transition group" }
    : { className: "flex items-center gap-4 p-4 rounded-lg border border-border bg-white" };

  return (
    <RowTag {...rowProps} data-testid={`worksheet-row-${w.worksheet_id}`}>
      <StatusDot status={status} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85">
          {w.module_id?.startsWith("bonus-") ? "Bonus" : `Module ${w.module_number}`} · W{w.worksheet_number}
        </div>
        <div className={[
          "mt-0.5 text-sm font-medium truncate",
          kind === "open" ? "text-foreground group-hover:text-[color:var(--brand-accent,#E91E63)] transition" : "text-foreground",
        ].join(" ")}>
          {w.title}
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground truncate">{w.outcome}</div>
      </div>
      <div className="text-right hidden sm:block">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85">
          {w.difficulty}
        </div>
        <div className="text-xs text-muted-foreground">{w.estimated_time}</div>
      </div>
      {kind === "open" ? (
        <ChevronRight className="w-4 h-4 text-muted-foreground/70 group-hover:text-foreground/80 shrink-0" />
      ) : kind === "unlock" ? (
        <Link
          to={action.to}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/70 px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold text-muted-foreground hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring max-w-[55%] sm:max-w-none"
          data-testid={`worksheet-row-${w.worksheet_id}-unlock`}
        >
          <Lock className="w-3 h-3 shrink-0" />
          <span className="truncate">{action.label}</span>
        </Link>
      ) : (
        <Link
          to={action.to}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-[color:var(--brand-accent,#E91E63)] px-2.5 py-1.5 text-[11px] sm:text-xs font-semibold text-white hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={`worksheet-row-${w.worksheet_id}-upgrade`}
        >
          <Zap className="w-3 h-3" /> Upgrade
        </Link>
      )}
    </RowTag>
  );
}

export default function WorkbookList() {
  const [progress, setProgress] = useState(null);
  const [worksheets, setWorksheets] = useState([]);
  const [error, setError] = useState(null);
  // Per-module checklist counts, keyed by module lms_slug (which matches
  // both moduleChecklists.js keys and user_progress.module_slug).
  const [checklistBySlug, setChecklistBySlug] = useState({});
  // Canonical module registry (`/api/modules`) — used to override the
  // hard-coded workbook-progress titles (e.g. "Bonus — AI Tools,
  // Celebrity Partnerships & Exit Strategy") with the current Menu
  // Directory-facing name (e.g. "AI Tools for CPG"). See the sync
  // logic under `moduleRegistryBySlug`.
  const [moduleRegistryBySlug, setModuleRegistryBySlug] = useState({});
  // Menu Directory overrides drive the per-child label + order + coming
  // soon / plan-gate state for `nav-modules`. Reading from context
  // (populated by AppShell's inner MenuVisibilityProvider) keeps the
  // Course Workbook and the sidebar in perfect sync — a single admin
  // edit updates both surfaces without a second API call here.
  const { childrenOverrides } = useMenuVisibility();
  const modulesOverride = (childrenOverrides || {})["nav-modules"] || {};

  // Filters
  const [q, setQ] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [difficulty, setDifficulty] = useState("");

  // Read the ?module= param on first render so the ModuleDetail deep-link
  // pre-filters the flat list.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setModuleId(params.get("module") || "");
    setDifficulty(params.get("difficulty") || "");
    setQ(params.get("q") || "");
  }, []);

  useEffect(() => {
    fetchProgress().then(setProgress).catch((e) => setError(formatError(e)));
  }, []);

  // Pull the canonical module registry so we can swap the workbook's
  // hard-coded titles for the Menu Directory-facing name (and pick up
  // any admin `label` override on the same doc). Missing / errored →
  // fall back to whatever `/progress` returned, so a network hiccup
  // never blanks out the workbook list.
  useEffect(() => {
    api.get("/modules")
      .then((r) => {
        const map = {};
        for (const m of r.data || []) {
          if (m?.slug) map[m.slug] = m;
        }
        setModuleRegistryBySlug(map);
      })
      .catch(() => setModuleRegistryBySlug({}));
  }, []);

  // Load per-module checklist progress from /progress/me (which returns
  // user_progress rows with checked_task_ids per module_slug).
  useEffect(() => {
    api.get("/progress/me").then((r) => {
      const rows = r.data?.rows || [];
      const map = {};
      for (const row of rows) {
        const slug = row.module_slug;
        const total = (MODULE_CHECKLISTS[slug] || []).length;
        if (!total) continue;
        const ids = new Set(MODULE_CHECKLISTS[slug].map((t) => t.id));
        const done = (row.checked_task_ids || []).filter((tid) => ids.has(tid)).length;
        map[slug] = { done, total };
      }
      // Also seed modules that have a checklist but no progress row yet.
      for (const [slug, tasks] of Object.entries(MODULE_CHECKLISTS)) {
        if (!map[slug] && tasks.length) map[slug] = { done: 0, total: tasks.length };
      }
      setChecklistBySlug(map);
    }).catch(() => { /* leave empty; counters just won't render */ });
  }, []);

  useEffect(() => {
    const params = {};
    if (moduleId) params.module_id = moduleId;
    if (difficulty) params.difficulty = difficulty;
    // NB: the free-text `q` is NOT sent to the backend anymore — the
    // server-side search still matches title + purpose + outcome, but
    // the product requirement is to only match Module title OR Worksheet
    // title. Filtering client-side keeps the scope guaranteed.
    fetchWorksheetSummaries(params).then(setWorksheets).catch(() => setWorksheets([]));
  }, [moduleId, difficulty]);

  // Build a module_id → module title lookup so the client-side title
  // filter can match "Company & Legal Setup" even when the user typed
  // it and every worksheet inside that module should be returned.
  const moduleTitleById = useMemo(() => {
    const out = {};
    for (const m of progress?.modules || []) {
      if (m.module_id) out[m.module_id] = (m.title || "").toLowerCase();
    }
    return out;
  }, [progress]);

  const visibleWorksheets = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return worksheets;
    return worksheets.filter((w) => {
      const wsTitle = (w.title || "").toLowerCase();
      const modTitle = moduleTitleById[w.module_id] || "";
      return wsTitle.includes(needle) || modTitle.includes(needle);
    });
  }, [q, worksheets, moduleTitleById]);

  // Build a module_id → canonical `/api/modules` row map so filtered
  // worksheet rows can resolve the SAME Open/Unlock/Upgrade action their
  // parent module card would show. This is what keeps the plan +
  // progress gating consistent when the view switches from card grid
  // to flat list.
  const moduleByWsLmsSlug = useMemo(() => {
    const out = {};
    for (const m of progress?.modules || []) {
      if (m.lms_slug) out[m.lms_slug] = m;
    }
    return out;
  }, [progress]);

  const resolveWorksheetAction = useMemo(() => {
    return (ws) => {
      const parent = moduleByWsLmsSlug[ws.lms_slug];
      if (!parent) {
        // Unknown parent → safest default is the worksheet detail page,
        // which has its own backend gate for a locked parent module.
        return { kind: "open", to: `/workbook/${ws.worksheet_id}` };
      }
      return resolveCardAction(parent, moduleRegistryBySlug[parent.lms_slug]);
    };
  }, [moduleByWsLmsSlug, moduleRegistryBySlug]);

  const statusByWs = useMemo(() => {
    const out = {};
    (progress?.modules || []).forEach((m) => {
      (m.worksheets || []).forEach((w) => {
        out[w.worksheet_id] = w.status || "not_started";
      });
    });
    return out;
  }, [progress]);

  // Decorate each workbook module with the Menu Directory `label` and
  // `order`, falling back to the canonical `/api/modules` name when
  // there's no admin override. Priority (highest wins):
  //   1. Menu Directory `children_overrides[nav-modules][slug].label`
  //   2. Canonical `/api/modules` `name` (e.g. "AI Tools for CPG")
  //   3. Workbook progress `title` (e.g. "Bonus — AI Tools, Celebrity
  //      Partnerships & Exit Strategy") — legacy fallback so nothing
  //      renders blank while `/api/modules` is still loading.
  // Ordering: sort ascending by the admin's `order`. Missing order
  // sinks to the bottom so newly added modules stay visible. Applied
  // AFTER the fallback so admins can reorder bonuses freely without
  // touching workbook code. Core modules keep their original ordering
  // when no explicit `order` was set — see `sortByAdminOrder`.
  const decoratedModules = useMemo(() => {
    const src = progress?.modules || [];
    const decorated = src.map((m) => {
      const patch = modulesOverride[m.lms_slug] || {};
      const canonical = moduleRegistryBySlug[m.lms_slug];
      const overrideLabel = patch.label
        || canonical?.name
        || m.title;
      // Strip any legacy "Bonus — " prefix from the fallback title so
      // the card's "BONUS" eyebrow (rendered separately) never
      // duplicates the word — matches sidebar behaviour.
      const cleanTitle = m.is_bonus
        ? (overrideLabel || "").replace(/^Bonus\s*[—-]\s*/i, "")
        : overrideLabel;
      return {
        ...m,
        title: cleanTitle,
        order: typeof patch.order === "number" ? patch.order : undefined,
      };
    });
    // Split-then-sort: core stays in its curriculum order unless the
    // admin has custom-ordered every module; bonus modules are always
    // ordered by the admin's `order` when present.
    const anyOrderedCore = decorated.filter((m) => !m.is_bonus).some((m) => typeof m.order === "number");
    const anyOrderedBonus = decorated.filter((m) => m.is_bonus).some((m) => typeof m.order === "number");
    const sortByAdminOrder = (arr) => arr.slice().sort((a, b) => {
      if (typeof a.order === "number" && typeof b.order === "number") return a.order - b.order;
      if (typeof a.order === "number") return -1;
      if (typeof b.order === "number") return 1;
      return 0;
    });
    return [
      ...(anyOrderedCore ? sortByAdminOrder(decorated.filter((m) => !m.is_bonus)) : decorated.filter((m) => !m.is_bonus)),
      ...(anyOrderedBonus ? sortByAdminOrder(decorated.filter((m) => m.is_bonus)) : decorated.filter((m) => m.is_bonus)),
    ];
  }, [progress, moduleRegistryBySlug, modulesOverride]);

  const coreModules = useMemo(() => (
    decoratedModules.filter((m) => !m.is_bonus)
  ), [decoratedModules]);
  const bonusModules = useMemo(() => (
    decoratedModules.filter((m) => m.is_bonus)
  ), [decoratedModules]);
  const totals = progress?.totals || {
    worksheets: 0, submitted: 0, in_progress: 0, not_started: 0,
    overall_pct: 0, total_words: 0, decisions_made: 0,
  };

  const filtersActive = !!(q || moduleId || difficulty);

  return (
    <AppShell title="Course Workbook">
      <div className="w-full pb-24">
        {/* Hero + totals */}
        <PageHero
          eyebrow="The founder's operating manual"
          icon={ClipboardList}
          title={<>Course <span className="text-grad">Workbook</span></>}
          subtitle="48 worksheets across 16 modules. Each one ends with a written decision you can act on."
        />

        <section className="mb-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { k: "Overall", v: `${totals.overall_pct || 0}%`, sub: `${totals.submitted}/${totals.worksheets} submitted` },
              { k: "In progress", v: totals.in_progress, sub: "worksheets started" },
              { k: "Words written", v: totals.total_words, sub: "across the workbook" },
              { k: "Decisions made", v: totals.decisions_made, sub: "in your log" },
            ].map((s) => (
              <div key={s.k} className="rounded-xl border border-border bg-white p-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground/85">{s.k}</div>
                <div className="mt-1 font-display text-2xl">{s.v}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{s.sub}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Filters */}
        <section className="mb-8 rounded-xl border border-border bg-white p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="relative w-full sm:flex-1 sm:min-w-[240px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70" />
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Module title or Worksheet title"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-border bg-white text-sm outline-none focus:border-foreground/40"
                data-testid="workbook-search"
              />
            </div>
            <select
              value={moduleId}
              onChange={(e) => setModuleId(e.target.value)}
              className="w-full sm:w-auto max-w-full px-3 py-2 rounded-md border border-border bg-white text-sm outline-none truncate"
              data-testid="workbook-module-filter"
            >
              <option value="">All modules</option>
              {(progress?.modules || []).map((m) => (
                <option key={m.module_id} value={m.module_id}>
                  {m.is_bonus ? "Bonus — " : `M${m.module_number}: `}{m.title}
                </option>
              ))}
            </select>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              className="w-full sm:w-auto px-3 py-2 rounded-md border border-border bg-white text-sm outline-none"
              data-testid="workbook-difficulty-filter"
            >
              <option value="">All difficulties</option>
              {DIFFICULTIES.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {filtersActive && (
              <button
                type="button"
                onClick={() => { setQ(""); setModuleId(""); setDifficulty(""); }}
                className="text-xs text-muted-foreground hover:text-foreground underline self-start sm:self-auto"
                data-testid="workbook-clear-filters"
              >
                Clear filters
              </button>
            )}
          </div>
        </section>

        {error && (
          <div className="surface p-6 mb-6 text-sm text-red-300">{error}</div>
        )}

        {/* When filters are active we show a flat, searchable list. Otherwise
            we show the two module-card sections (core + bonus). */}
        {filtersActive ? (
          <section>
            <div className="mb-3 text-[11px] uppercase tracking-widest text-muted-foreground">
              {visibleWorksheets.length} match{visibleWorksheets.length === 1 ? "" : "es"}
            </div>
            <div className="space-y-2">
              {visibleWorksheets.map((w) => (
                <WorksheetRow
                  key={w.worksheet_id}
                  w={w}
                  statusByWs={statusByWs}
                  action={resolveWorksheetAction(w)}
                />
              ))}
              {visibleWorksheets.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-10">
                  No worksheets match those filters.
                </div>
              )}
            </div>
          </section>
        ) : (
          <>
            <section className="mb-12">
              <div className="mb-4 flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground">
                <ListChecks className="w-3.5 h-3.5" /> Core curriculum
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {coreModules.map((m) => (
                  <ModuleCard
                    key={m.module_id}
                    mod={m}
                    checklist={checklistBySlug[m.lms_slug]}
                    action={resolveCardAction(m, moduleRegistryBySlug[m.lms_slug])}
                  />
                ))}
              </div>
            </section>

            {bonusModules.length > 0 && (
              <section>
                <div className="mb-4 flex items-center gap-2 text-[11px] uppercase tracking-widest text-purple-300">
                  <Sparkles className="w-3.5 h-3.5" /> Bonus modules
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {bonusModules.map((m) => (
                    <ModuleCard
                      key={m.module_id}
                      mod={m}
                      checklist={checklistBySlug[m.lms_slug]}
                      action={resolveCardAction(m, moduleRegistryBySlug[m.lms_slug])}
                    />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
